/**
 * CareLogix™ ORACLE — AdminOne Live Data Control Center™ (additive).
 *
 * RBAC enforced on backend. Renders per-domain provider readiness, audit
 * trail, and aggregate counts. Truthfully shows PROVIDER_NOT_CONFIGURED
 * for every connector that isn't probed and AVAILABLE.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getAdminToken } from "@/api/adminOneClient";
import OracleDataModeBadge from "@/components/oracle/OracleDataModeBadge";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";

async function _get(path, auth = false) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const t = getAdminToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  const r = await fetch(`${BACKEND}${path}`, { headers });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

const DOMAIN_ICONS = {
  SPORTS:     "🏀",
  MARKETS:    "📈",
  NEWS:       "📰",
  GRANTS:     "💰",
  CONTRACTS:  "📋",
  RESEARCH:   "🔬",
  HEALTHCARE: "🏥",
  WEARABLES:  "⌚",
  CAPITAL:    "💼",
  FOODRX:     "🥗",
  GOVERNANCE: "🏛",
};

const S = {
  page: { padding: "24px 28px 80px", background: "linear-gradient(160deg, #050b09 0%, #06231b 60%, #050b09 100%)", color: "#dfe3eb", minHeight: "100vh", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 18 },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 26, fontFamily: "'Cinzel', serif", letterSpacing: "0.02em" },
  sub: { color: "#cbd5e1", marginTop: 8, fontSize: 12.5, maxWidth: 820, lineHeight: 1.5 },
  link: { color: "#c5a059", textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.1em", marginLeft: 12 },
  kpis: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginBottom: 18 },
  kpi: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 12, padding: "12px 14px" },
  kpiLabel: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.14em" },
  kpiValue: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 26, marginTop: 6 },
  section: { background: "rgba(13,26,15,0.65)", border: "1px solid rgba(245,200,66,0.20)", borderRadius: 14, padding: 16, marginBottom: 16 },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, margin: 0, marginBottom: 8 },
  sectionSub: { color: "#cbd5e1", fontSize: 12, marginBottom: 10 },
  card: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 10, padding: "10px 12px" },
  pill: { display: "inline-block", padding: "3px 9px", borderRadius: 999, border: "1px solid", fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em" },
  btn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "7px 13px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { textAlign: "left", color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.12em", padding: "8px 8px", borderBottom: "1px solid rgba(245,200,66,0.18)" },
  td: { padding: "9px 8px", borderBottom: "1px solid rgba(255,255,255,0.05)", color: "#cbd5e1" },
  compliance: { marginTop: 20, padding: "12px 14px", background: "rgba(252,211,77,0.06)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 12, color: "#fde68a", fontSize: 12 },
};

function StatusPill({ status }) {
  const color =
    status === "AVAILABLE"                 ? "#7AC860" :
    status === "LIVE_VERIFIED"             ? "#7AC860" :
    status === "PROVIDER_NOT_CONFIGURED"   ? "#ef4444" :
    status === "STUB_AWAITING_IMPLEMENTATION" ? "#94a3b8" :
    "#fcd34d";
  return <span style={{ ...S.pill, color, borderColor: color, background: `${color}1a` }}>{status}</span>;
}

export default function AdminOneLiveDataCenter() {
  const [providers, setProviders] = useState(null);
  const [audit, setAudit]         = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [demoEnvelope, setDemoEnvelope] = useState(null);
  const _once = useRef(false);

  const loadProviders = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await _get("/api/oracle/live-data/providers");
      setProviders(r);
    } catch (_e) {
      setProviders({ providers: {}, aggregate: { available: 0, not_configured: 0, total: 0 } });
    } finally { setRefreshing(false); }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const r = await _get("/api/oracle/live-data/audit?limit=50", true);
      setAudit(r.events || []);
    } catch (_e) { setAudit([]); }
  }, []);

  useEffect(() => {
    if (_once.current) return; _once.current = true;
    loadProviders(); loadAudit();
  }, [loadProviders, loadAudit]);

  const runSportsProbe = useCallback(async () => {
    try {
      const r = await _get("/api/oracle/live-data/sports/score?league=nba&team_a=Knicks&team_b=Spurs&date=yesterday");
      setDemoEnvelope(r);
      loadAudit();
    } catch (_e) {
      setDemoEnvelope({ data_mode: "ERROR", domain: "SPORTS", truthfulness_notice: "Probe failed." });
    }
  }, [loadAudit]);

  const agg = (providers && providers.aggregate) || { available: 0, not_configured: 0, total: 0 };
  const failedQueries     = audit.filter(e => ["live_data_query_source_unavailable","sports_score_query_no_provider","live_data_query_blocked_fake_answer"].includes(e.event));
  const knowledgeQueries  = audit.filter(e => e.event === "live_data_query_knowledge_only");
  const liveQueries       = audit.filter(e => ["live_data_query_live_verified","sports_score_query_verified"].includes(e.event));
  const lastCheck         = audit[0] && audit[0].timestamp;

  const byDomain = (providers && providers.providers) || {};

  return (
    <main style={S.page} data-testid="adminone-live-data-page">
      <header style={S.header}>
        <div>
          <div style={S.eyebrow}>ADMINONE · LIVE DATA CONTROL CENTER™</div>
          <h1 style={S.title}>Live Data Registry — Provider Readiness & Truthfulness Audit</h1>
          <p style={S.sub}>
            ORACLE never returns <code>LIVE_VERIFIED</code> without an actual provider probe. Every connector below truthfully reports
            its current status; missing keys surface as <code>PROVIDER_NOT_CONFIGURED</code> rather than silently degrading to a fabricated answer.
          </p>
        </div>
        <div>
          <Link to="/adminone/membership-control" style={S.link} data-testid="link-membership">Membership Control →</Link>
          <Link to="/adminone/telemetry" style={S.link} data-testid="link-telemetry">Telemetry →</Link>
        </div>
      </header>

      <section style={S.kpis} data-testid="live-data-kpis">
        <div style={S.kpi} data-testid="kpi-total">
          <div style={S.kpiLabel}>TOTAL PROVIDERS</div>
          <div style={S.kpiValue}>{agg.total}</div>
        </div>
        <div style={S.kpi} data-testid="kpi-available">
          <div style={S.kpiLabel}>AVAILABLE</div>
          <div style={{ ...S.kpiValue, color: "#7AC860" }}>{agg.available}</div>
        </div>
        <div style={S.kpi} data-testid="kpi-missing">
          <div style={S.kpiLabel}>NOT CONFIGURED</div>
          <div style={{ ...S.kpiValue, color: "#ef4444" }}>{agg.not_configured}</div>
        </div>
        <div style={S.kpi} data-testid="kpi-live-queries">
          <div style={S.kpiLabel}>LIVE VERIFIED QUERIES</div>
          <div style={S.kpiValue}>{liveQueries.length}</div>
        </div>
        <div style={S.kpi} data-testid="kpi-failed">
          <div style={S.kpiLabel}>FAILED / NO_PROVIDER</div>
          <div style={S.kpiValue}>{failedQueries.length}</div>
        </div>
        <div style={S.kpi} data-testid="kpi-knowledge">
          <div style={S.kpiLabel}>KNOWLEDGE-ONLY QUERIES</div>
          <div style={S.kpiValue}>{knowledgeQueries.length}</div>
        </div>
        <div style={S.kpi} data-testid="kpi-last-check">
          <div style={S.kpiLabel}>LAST PROVIDER CHECK</div>
          <div style={{ ...S.kpiValue, fontSize: 14, marginTop: 12 }}>{lastCheck ? new Date(lastCheck).toLocaleString() : "—"}</div>
        </div>
        <div style={S.kpi} data-testid="kpi-actions">
          <div style={S.kpiLabel}>ACTIONS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            <button type="button" style={S.btn} onClick={loadProviders} disabled={refreshing} data-testid="btn-refresh-providers">
              {refreshing ? "Refreshing…" : "Refresh status"}
            </button>
            <button type="button" style={S.btn} onClick={runSportsProbe} data-testid="btn-probe-sports">
              Probe ESPN live
            </button>
          </div>
        </div>
      </section>

      {demoEnvelope && (
        <section style={S.section} data-testid="live-data-probe-result">
          <h2 style={S.sectionTitle}>Last live probe (ESPN — Knicks vs Spurs, yesterday)</h2>
          <OracleDataModeBadge envelope={demoEnvelope} testid="probe-data-mode-badge" />
          {demoEnvelope.winner && (
            <p style={{ color: "#fde68a", marginTop: 10, fontSize: 13 }}>
              Winner: <strong>{demoEnvelope.winner}</strong> · Final: <strong>{demoEnvelope.final_score}</strong> · Status: {demoEnvelope.status}
            </p>
          )}
        </section>
      )}

      {/* Per-domain provider grid */}
      {Object.entries(byDomain).map(([dom, items]) => (
        <section key={dom} style={S.section} data-testid={`domain-${dom}`}>
          <h2 style={S.sectionTitle}>{DOMAIN_ICONS[dom] || "🔌"} {dom} <span style={{ color: "#94a3b8", fontSize: 12, fontFamily: "'Space Mono', monospace", marginLeft: 8 }}>({items.length} provider{items.length === 1 ? "" : "s"})</span></h2>
          {items.length === 0 ? (
            <p style={{ color: "#fcd34d", fontFamily: "'Space Mono', monospace", fontSize: 11 }} data-testid={`empty-${dom}`}>NO_DATA_AVAILABLE</p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
              {items.map((p) => (
                <div key={p.key} style={S.card} data-testid={`provider-${dom}-${p.key}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <strong style={{ color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 14 }}>{p.label}</strong>
                    <StatusPill status={p.status} />
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 6, fontFamily: "'Space Mono', monospace" }}>
                    env: {p.env || "—"} · {p.needs_key ? "key required" : "public"}
                  </div>
                  {p.reason && <div style={{ color: "#cbd5e1", fontSize: 11, marginTop: 4 }}>{p.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </section>
      ))}

      {/* Audit trail */}
      <section style={S.section} data-testid="live-data-audit">
        <h2 style={S.sectionTitle}>Audit trail</h2>
        <p style={S.sectionSub}>Last {audit.length} events.</p>
        {audit.length === 0 ? (
          <p style={{ color: "#fcd34d", fontFamily: "'Space Mono', monospace", fontSize: 11 }} data-testid="audit-empty">NO_DATA_AVAILABLE</p>
        ) : (
          <table style={S.table} data-testid="audit-table">
            <thead>
              <tr>
                <th style={S.th}>EVENT</th>
                <th style={S.th}>DOMAIN</th>
                <th style={S.th}>MODE</th>
                <th style={S.th}>PROVIDER</th>
                <th style={S.th}>QUERY</th>
                <th style={S.th}>WHEN</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((e, i) => (
                <tr key={i} data-testid={`audit-row-${i}`}>
                  <td style={S.td}><span style={{ ...S.pill, color: "#c5a059", borderColor: "#c5a059", background: "rgba(197,160,89,0.10)" }}>{e.event}</span></td>
                  <td style={S.td}>{e.domain}</td>
                  <td style={S.td}><StatusPill status={e.data_mode || "ERROR"} /></td>
                  <td style={S.td}>{e.provider_key || "—"}</td>
                  <td style={S.td} title={e.query}>{(e.query || "").slice(0, 60)}</td>
                  <td style={S.td}>{e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div style={S.compliance} data-testid="live-data-compliance">
        Truthfulness Mandate: ORACLE blocks any LLM-fabricated answer in the
        SPORTS, MARKETS, NEWS, GRANTS, and CONTRACTS domains when no live
        provider is configured. Every live-required query writes an audit
        event with the response mode (LIVE_VERIFIED / PROVIDER_NOT_CONFIGURED
        / SOURCE_UNAVAILABLE / ERROR) — viewable above.
      </div>
    </main>
  );
}
